import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { CheckCircle, ClipboardCheck, RotateCcw, Wind, Search, LucideIcon, ChevronDown, ArrowRight } from "lucide-react";
import { SiteLayout } from "@/components/site/SiteLayout";
import { CTASection } from "@/components/site/CTASection";
import { SEOHead } from "@/components/SEOHead";
import { BreadcrumbSchema, FAQSchema } from "@/components/SchemaOrg";
import { ProcessInfographic } from "@/components/site/ProcessInfographic";

// Custom icons - Red versions for prestations
import voitureRedIcon from "@/assets/icons/voiture-red.webp";
import motoRedIcon from "@/assets/icons/moto-red.webp";
import utilitaireRedIcon from "@/assets/icons/utilitaire-red.webp";
import campingcarRedIcon from "@/assets/icons/campingcar-red.webp";
import quatreQuatreRedIcon from "@/assets/icons/4x4-red.webp";
import collectionRedIcon from "@/assets/icons/collection-red.webp";
import specifiqueRedIcon from "@/assets/icons/specifique-red.webp";
import vanRedIcon from "@/assets/icons/van-red.webp";
import hybrideRedIcon from "@/assets/icons/hybride-red.webp";
import voituretteRedIcon from "@/assets/icons/voiturette-red.webp";
import bancControleCentre from "@/assets/centre/banc-controle-centre.webp";

// Blue versions for vehicle categories
import voitureBlueIcon from "@/assets/icons/voiture-blue.webp";
import motoBlueIcon from "@/assets/icons/moto-blue.webp";
import utilitaireBlueIcon from "@/assets/icons/utilitaire-blue.webp";

interface Prestation {
  icon: LucideIcon | string;
  title: string;
  description: string;
  price: string;
  originalPrice?: string;
  details: string[];
  categories: string[];
  priceByCategory?: Record<string, { price: string; originalPrice?: string }>;
}

// Contrôles de base - applies to all
const controlesBase: Prestation[] = [
  {
    icon: ClipboardCheck,
    title: "Contrôle technique périodique",
    description: "Contrôle réglementaire obligatoire pour tous les véhicules de plus de 4 ans.",
    price: "À partir de 73€",
    originalPrice: "80€",
    details: ["133 points de contrôle", "Résultat immédiat", "Procès-verbal officiel"],
    categories: ["all", "vl", "L"],
    priceByCategory: {
      L: { price: "70€" },
      vl: { price: "À partir de 73€", originalPrice: "80€" },
      all: { price: "À partir de 73€", originalPrice: "80€" },
    }
  },
  {
    icon: RotateCcw,
    title: "Contre-visite",
    description: "Suite à un contrôle défavorable, repassez uniquement les points défaillants.",
    price: "À partir de 30€",
    details: ["Client centre : 30€", "Partielle externe : 50€", "Complète externe : 80€"],
    categories: ["all", "vl", "L"]
  },
  {
    icon: Wind,
    title: "Contrôle pollution",
    description: "Analyse des émissions polluantes de votre véhicule essence ou diesel.",
    price: "35€",
    details: ["Analyseur homologué", "Normes antipollution", "Rapport détaillé"],
    categories: ["all", "vl"]
  },
  {
    icon: Search,
    title: "Pré-contrôle volontaire",
    description: "Vérification préventive avant le contrôle officiel pour anticiper les défauts.",
    price: "Sur devis",
    details: ["Sans engagement", "Conseils réparation", "Préparation optimale"],
    categories: ["all", "vl", "L"]
  },
];

// Contrôles spécifiques par type de véhicule - matching official Autosur prestations
const controlesSpecifiques: Prestation[] = [
  // Catégorie VL
  {
    icon: voitureRedIcon,
    title: "Véhicule Essence / Diesel",
    description: "Contrôle technique pour voitures particulières essence ou diesel.",
    price: "73€",
    originalPrice: "80€",
    details: ["Tous véhicules thermiques", "Analyse pollution incluse", "Résultat immédiat"],
    categories: ["vl"]
  },
  {
    icon: utilitaireRedIcon,
    title: "Véhicule utilitaire Léger",
    description: "Contrôle pour véhicules utilitaires légers et fourgonnettes.",
    price: "79€",
    originalPrice: "86€",
    details: ["CTTE & Dérivés VP", "Capacité adaptée", "Tous tonnages"],
    categories: ["vl"]
  },
  {
    icon: campingcarRedIcon,
    title: "Véhicule Gros Volume / Camping Car",
    description: "Contrôle pour camping-cars, fourgons aménagés et véhicules gros volume.",
    price: "84€",
    originalPrice: "91€",
    details: ["Gabarit adapté", "Équipements spécifiques", "Homologation VASP"],
    categories: ["vl"]
  },
  {
    icon: hybrideRedIcon,
    title: "Hybride / Électrique",
    description: "Contrôle adapté aux spécificités des motorisations électriques et hybrides.",
    price: "79€",
    originalPrice: "86€",
    details: ["Équipement dédié", "Personnel formé", "Sécurité haute tension"],
    categories: ["vl"]
  },
  {
    icon: quatreQuatreRedIcon,
    title: "Véhicule 4x4 / GPL",
    description: "Contrôle adapté aux véhicules tout-terrain, SUV et véhicules GPL/GNV.",
    price: "88€",
    originalPrice: "95€",
    details: ["Équipement adapté", "Hauteur de caisse", "Homologation gaz"],
    categories: ["vl"]
  },
  {
    icon: collectionRedIcon,
    title: "Véhicules de collection",
    description: "Contrôle adapté aux véhicules de plus de 30 ans avec carte grise collection.",
    price: "73€",
    originalPrice: "80€",
    details: ["Expertise spécifique", "Respect de l'authenticité", "Points adaptés"],
    categories: ["vl"]
  },
  // Catégorie L
  {
    icon: motoRedIcon,
    title: "Moto / Scooter / Quad",
    description: "Motos, scooters, mobylettes, tricycles, quads et side-cars.",
    price: "70€",
    details: ["Obligatoire depuis 2024", "Toutes cylindrées", "Équipements sécurité"],
    categories: ["L"]
  },
  {
    icon: voituretteRedIcon,
    title: "Voiturettes (VSP)",
    description: "Contrôle pour véhicules sans permis et quadricycles légers.",
    price: "70€",
    details: ["VSP homologués", "Quadricycles légers", "Conformité CE"],
    categories: ["L"]
  },
];

// Vehicle category filters - matching official Autosur categories
const vehicleFilters = [
  {
    id: "all",
    iconSrc: voitureBlueIcon,
    title: "Toutes catégories",
    shortTitle: "Tous"
  },
  {
    id: "vl",
    iconSrc: voitureBlueIcon,
    title: "Catégorie VL",
    shortTitle: "VL",
    description: "Voitures particulières, utilitaires, ..."
  },
  {
    id: "L",
    iconSrc: motoBlueIcon,
    title: "Catégorie L",
    shortTitle: "Cat. L",
    description: "2 roues, 3 roues, quads, voiturettes"
  }
];

const PrestationsPage = () => {
  const [activeFilter, setActiveFilter] = useState("all");

  // Filter prestations based on active filter
  const filteredBase = controlesBase
    .filter(p => activeFilter === "all" || p.categories.includes(activeFilter) || p.categories.includes("all"))
    .map(p => {
      if (p.priceByCategory && p.priceByCategory[activeFilter]) {
        const override = p.priceByCategory[activeFilter];
        return { ...p, price: override.price, originalPrice: override.originalPrice };
      }
      return p;
    });
  
  const filteredSpecifiques = controlesSpecifiques.filter(p => 
    activeFilter === "all" || p.categories.includes(activeFilter)
  );

  const prestationsFAQ = [
    { question: "Quel est le prix du contrôle technique à Montreuil-sous-Bois ?", answer: "Le contrôle technique voiture est à partir de 73€ en réservant en ligne (au lieu de 80€). Le contrôle moto est à 70€. Utilitaire : 79€ en ligne (86€ standard). Camping-car/gros volume : 84€ en ligne (91€ standard). Véhicule de collection : 73€. Pas de frais cachés, tarifs identiques pour tous." },
    { question: "Combien de temps dure un contrôle technique ?", answer: "Un contrôle technique standard dure environ 30 à 45 minutes pour une voiture, 20-30 minutes pour une moto, 30-45 minutes pour un utilitaire, et 45-60 minutes pour un véhicule de collection ou gros volume. Résultat immédiat avec procès-verbal officiel à télécharger directement." },
    { question: "Quels défauts courants sont détectés ?", answer: "Nous inspectons 133 points réglementaires : freins (usure, efficacité), suspension (amortisseurs, ressorts), éclairage (phares, feux, clignotants), pollution (émissions), pneus (usure, pression), vitres (fissures), ceintures de sécurité (fonctionnement), rétroviseurs, essuie-glaces, etc. Les défauts critiques sont identifiés immédiatement." },
    { question: "Que faire si j'échoue mon contrôle technique ?", answer: "Vous avez 2 mois pour faire une contre-visite. Celle-ci ne porte que sur les points défaillants (pas besoin de tout reinspecter). Contre-visite client centre à partir de 30€, partielle externe à partir de 50€, complète externe à partir de 80€. Nous vous aidons à identifier les défauts à corriger." },
    { question: "Puis-je passer un contrôle technique sans rendez-vous ?", answer: "Oui ! Vous pouvez venir le samedi sans RDV selon disponibilités. Sinon, réservez en ligne 24/7 pour garantir votre créneau et bénéficier du tarif web (économisez 7€)." },
    { question: "Quels sont les horaires du centre Autosur Montreuil ?", answer: "Notre centre est ouvert du lundi au vendredi de 8h30 à 18h30, et le samedi de 9h00 à 13h00. Fermé le dimanche. Réservation en ligne disponible 24h/24, 7j/7." },
    { question: "Quels types de véhicules pouvez-vous contrôler ?", answer: "Nous contrôlons tous types de véhicules : voitures particulières, motos et scooters (depuis 2024), utilitaires légers, camping-cars, véhicules de collection, 4x4, hybrides, électriques, GPL/GNV et voiturettes." },
    { question: "Quels documents apporter pour le contrôle technique ?", answer: "Vous devez obligatoirement présenter la carte grise (certificat d'immatriculation) du véhicule. Pour une contre-visite, apportez également l'ancien procès-verbal de contrôle technique. C'est tout, pas besoin d'autre document !" },
    { question: "Quand dois-je faire mon contrôle technique ?", answer: "Le premier CT est obligatoire dans les 6 mois précédant le 4e anniversaire de la première mise en circulation, puis tous les 2 ans. Ne pas faire votre CT à temps peut entraîner une amende de 135€ et l'immobilisation du véhicule." },
    { question: "Puis-je faire un CT pour un véhicule électrique ?", answer: "Oui ! Le contrôle technique est identique aux véhicules essence/diesel. Notre centre dispose d'équipements adaptés et notre équipe est formée aux spécificités des véhicules électriques et hybrides. Tarifs dès 79€ en ligne." },
    { question: "Le CT moto est-il vraiment obligatoire ?", answer: "Oui, depuis avril 2024, le contrôle technique est obligatoire pour tous les véhicules de catégorie L : motos, scooters, quads, voiturettes. Le calendrier dépend de la date d'immatriculation. Tarifs dès 70€ chez Autosur Montreuil." },
    { question: "Avez-vous du parking ?", answer: "Oui ! Nous disposons d'un parking gratuit et sécurisé directement sur place. Notre centre est également accessible aux personnes à mobilité réduite (accès PMR complet)." },
  ];

  return (
    <SiteLayout>
      <SEOHead
        title="Prestations et Tarifs"
        description="Nos prestations et tarifs : contrôle technique voiture dès 73€, moto 70€, utilitaire, collection. Contre-visite gratuite. RDV en ligne 24/7."
        path="/prestations"
      />
      <BreadcrumbSchema items={[{ name: "Accueil", href: "/" }, { name: "Prestations", href: "/prestations" }]} />
      <FAQSchema items={prestationsFAQ} />
      {/* Hero */}
      <section className="relative py-24 bg-gradient-to-br from-autosur-navy to-autosur-blue overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div 
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url(${bancControleCentre})` }}
          />
        </div>
        <div className="container mx-auto px-6 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto"
          >
            <span className="text-autosur-red font-heading font-semibold tracking-widest text-sm mb-4 block">
              NOS SERVICES
            </span>
            <h1 className="font-heading text-4xl md:text-5xl font-bold text-white mb-6">
              Contrôle Technique à Montreuil : Tous nos Services et Tarifs
            </h1>
            <p className="font-body text-white/80 text-lg">
              Contrôle technique complet à Montreuil-sous-Bois : voitures, motos, utilitaires, véhicules de collection. Détection des 133 points de contrôle réglementaires. Tarifs transparents dès 73€ en ligne. Résultat immédiat, procès-verbal officiel. Centre agréé, 343 avis 4.9/5.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Filter Section */}
      <section className="py-12 bg-background border-b">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-8"
          >
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-autosur-navy mb-2">
              Catégories de véhicules
            </h2>
            <p className="font-body text-muted-foreground">
              Sélectionnez votre type de véhicule pour découvrir les prestations adaptées
            </p>
          </motion.div>

          {/* Filter buttons - 4 columns on mobile, 4 on desktop */}
          <div className="grid grid-cols-4 gap-2 sm:gap-4 max-w-2xl mx-auto">
            {vehicleFilters.map((filter, index) => (
              <motion.button
                key={filter.id}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.3, delay: index * 0.05 }}
                onClick={() => setActiveFilter(filter.id)}
                className={`flex flex-col items-center gap-2 p-3 sm:p-4 rounded-xl transition-all ${
                  activeFilter === filter.id
                    ? "bg-autosur-blue text-white shadow-lg"
                    : "bg-white border border-gray-200 text-autosur-navy hover:border-autosur-blue/50 hover:bg-autosur-blue/5"
                }`}
              >
                <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-lg flex items-center justify-center p-1 ${
                  activeFilter === filter.id ? "bg-white/20" : "bg-autosur-blue/10"
                }`}>
                  <img 
                    src={filter.iconSrc} 
                    alt={filter.title} 
                    className={`w-full h-full object-contain ${activeFilter === filter.id ? "brightness-0 invert" : ""}`}
                  />
                </div>
                <span className="font-heading font-semibold text-xs sm:text-sm">
                  <span className="hidden sm:inline">{filter.title}</span>
                  <span className="sm:hidden">{filter.shortTitle}</span>
                </span>
              </motion.button>
            ))}
          </div>
        </div>
      </section>

      {/* Contrôles de base */}
      <section className="py-16 bg-background">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-10"
          >
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-autosur-navy mb-2">
              Contrôles de base
            </h2>
            <p className="font-body text-muted-foreground">
              Services essentiels pour tous les véhicules
            </p>
          </motion.div>

          <AnimatePresence mode="wait">
            <motion.div
              key={activeFilter + "-base"}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.3 }}
              className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6"
            >
              {filteredBase.map((prestation, index) => (
                <motion.div
                  key={prestation.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.05 }}
                  className="bg-white rounded-xl p-5 shadow-sm border border-gray-100 hover:shadow-md hover:border-autosur-blue/30 transition-all"
                >
                  <div className="w-10 h-10 mb-3 bg-autosur-red/10 rounded-lg flex items-center justify-center">
                    {typeof prestation.icon === 'string' ? (
                      <img src={prestation.icon} alt={prestation.title} className="w-6 h-6 object-contain" />
                    ) : (
                      <prestation.icon className="w-5 h-5 text-autosur-red" strokeWidth={1.5} />
                    )}
                  </div>

                  <h3 className="font-heading font-bold text-autosur-navy text-sm mb-1">
                    {prestation.title}
                  </h3>
                  <p className="font-body text-muted-foreground text-xs mb-3 line-clamp-2">
                    {prestation.description}
                  </p>

                  <div className="mb-3">
                    <span className="font-heading font-bold text-autosur-blue text-sm">{prestation.price}</span>
                    {prestation.originalPrice && (
                      <span className="font-body text-muted-foreground text-xs ml-2 line-through">{prestation.originalPrice}</span>
                    )}
                  </div>

                  <ul className="space-y-1">
                    {prestation.details.map((detail) => (
                      <li key={detail} className="flex items-center gap-2 text-xs">
                        <CheckCircle className="w-3 h-3 text-green-500 flex-shrink-0" />
                        <span className="font-body text-muted-foreground">{detail}</span>
                      </li>
                    ))}
                  </ul>
                </motion.div>
              ))}
            </motion.div>
          </AnimatePresence>
        </div>
      </section>

      {/* Contrôles spécifiques */}
      {filteredSpecifiques.length > 0 && (
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-center mb-10"
            >
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-autosur-navy mb-2">
                Contrôles spécifiques
              </h2>
              <p className="font-body text-muted-foreground">
                Adaptés à chaque type de véhicule
              </p>
            </motion.div>

            <AnimatePresence mode="wait">
              <motion.div
                key={activeFilter + "-specifiques"}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.3 }}
                className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-6"
              >
                {filteredSpecifiques.map((prestation, index) => (
                  <motion.div
                    key={prestation.title}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: index * 0.05 }}
                    className="bg-white rounded-xl p-5 shadow-sm border border-gray-100 hover:shadow-md hover:border-autosur-blue/30 transition-all"
                  >
                    <div className="w-10 h-10 mb-3 bg-autosur-red/10 rounded-lg flex items-center justify-center">
                      {typeof prestation.icon === 'string' ? (
                        <img src={prestation.icon} alt={prestation.title} className="w-6 h-6 object-contain" />
                      ) : (
                        <prestation.icon className="w-5 h-5 text-autosur-red" strokeWidth={1.5} />
                      )}
                    </div>

                    <h3 className="font-heading font-bold text-autosur-navy text-sm mb-1">
                      {prestation.title}
                    </h3>
                    <p className="font-body text-muted-foreground text-xs mb-3 line-clamp-2">
                      {prestation.description}
                    </p>

                    <div className="mb-3">
                      <span className="font-heading font-bold text-autosur-blue text-sm">{prestation.price}</span>
                      {prestation.originalPrice && (
                        <span className="font-body text-muted-foreground text-xs ml-2 line-through">{prestation.originalPrice}</span>
                      )}
                    </div>

                    <ul className="space-y-1">
                      {prestation.details.map((detail) => (
                        <li key={detail} className="flex items-center gap-2 text-xs">
                          <CheckCircle className="w-3 h-3 text-green-500 flex-shrink-0" />
                          <span className="font-body text-muted-foreground">{detail}</span>
                        </li>
                      ))}
                    </ul>
                  </motion.div>
                ))}
              </motion.div>
            </AnimatePresence>
          </div>
        </section>
      )}

      {/* Process Infographic */}
      <ProcessInfographic />

      {/* Pricing info */}
      <section className="py-16 bg-background">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="max-w-2xl mx-auto"
          >
            <div className="relative bg-autosur-grey-light rounded-2xl p-8 md:p-10">
              {/* Corner accents */}
              <div className="absolute top-0 left-0 w-8 h-8 border-l-2 border-t-2 border-autosur-navy rounded-tl-2xl" />
              <div className="absolute bottom-0 right-0 w-8 h-8 border-r-2 border-b-2 border-autosur-navy rounded-br-2xl" />
              
              <div className="flex flex-col items-center text-center gap-4">
                <h2 className="font-heading text-xl md:text-2xl font-bold text-autosur-navy">
                  Tarifs préférentiels en ligne
                </h2>
                <p className="font-body text-muted-foreground text-sm">
                  Réservez votre contrôle technique en ligne et profitez de nos tarifs web.
                </p>

                <div className="flex flex-col sm:flex-row gap-3">
                  <Link
                    to="/contact"
                    className="inline-flex items-center gap-2 px-6 py-3 bg-autosur-red text-white font-heading font-semibold rounded-full hover:bg-autosur-red-light transition-colors shadow-md"
                  >
                    Réserver en ligne
                  </Link>
                  <a
                    href="tel:+33149889240"
                    className="inline-flex items-center gap-2 px-6 py-3 bg-white border-2 border-autosur-navy text-autosur-navy font-heading font-semibold rounded-full hover:bg-autosur-navy hover:text-white transition-all"
                  >
                    Appeler le centre
                  </a>
                </div>

                <p className="font-body text-muted-foreground text-xs mt-2">
                  (*) Offre non cumulable. Les tarifs sont indicatifs et peuvent varier selon le type de véhicule.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl mx-auto"
          >
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-autosur-navy mb-8 text-center">
              Questions fréquentes sur nos prestations
            </h2>
            <div className="space-y-4">
              {prestationsFAQ.map((faq, index) => (
                <details key={index} className="bg-white rounded-xl border border-gray-100 shadow-sm group">
                  <summary className="flex items-center justify-between p-5 cursor-pointer font-heading font-semibold text-autosur-navy text-sm hover:text-autosur-blue transition-colors">
                    {faq.question}
                    <ChevronDown className="w-5 h-5 text-muted-foreground group-open:rotate-180 transition-transform flex-shrink-0 ml-4" />
                  </summary>
                  <div className="px-5 pb-5 pt-0">
                    <p className="font-body text-muted-foreground text-sm">{faq.answer}</p>
                  </div>
                </details>
              ))}
            </div>
            <div className="text-center mt-8">
              <Link
                to="/faq"
                className="inline-flex items-center gap-2 text-autosur-blue font-heading font-semibold hover:text-autosur-navy transition-colors"
              >
                Voir toutes les questions fréquentes
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      <CTASection />
    </SiteLayout>
  );
};

export default PrestationsPage;
